import { HTMLAttributes, useMemo } from 'react'
import {
  FinnishTailoringButton,
  StartTailoringButton,
  TailoringFromPublicJobBoardStep2Buttons,
} from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import Icon24 from 'builder/components/Icon'
import { navigate } from 'builder/modules/navigate'
import { useI18n } from 'builder/hooks/useI18n'
import { AutoTailoringFromPublicJobBoardStep, AutoTailoringMode, selectors } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'

import { SegmentControls } from 'builder/components/SegmentControls'
import { ButtonSize, ButtonV2 } from 'builder/components/Button'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useStepByStepFeature } from '../../StepByStepFlow/hooks/useStepByStepFeature'
import { StyledSegmentControl } from '../../TopNavBar/styles'
import { useTabNavigation } from '../../TopNavBar/hooks'
import {
  BackButton,
  BackButtonPlaceholder,
  BackIcon,
  Center,
  Container,
  Step,
  Title,
  WrapperStartTailoringButton,
} from './styles'

type Props = HTMLAttributes<HTMLDivElement> & {
  isNewBuilderEnabled?: boolean
}

export const TailoringFromPublicJobBoardHeader = (props: Props) => {
  const { isNewBuilderEnabled, ...rest } = props
  const { i18n } = useI18n()
  const { step, mode } = useTypedSelector(selectors.autoTailoringFromPublicJobBoard)
  const selectedPrefillOption = useTypedSelector(selectors.selectedPrefillOption)
  const { isRbpStepFlowActive } = useStepByStepFeature()
  const { handleTabChange, editorPanelType, getTabItems } = useTabNavigation()
  const tabItems = getTabItems()
  const { isAboveTablet } = useMediaQueries()

  const isTabsVisible =
    isNewBuilderEnabled &&
    !selectedPrefillOption &&
    step === AutoTailoringFromPublicJobBoardStep.step1

  const showBackToTemplatesButtonText =
    step === AutoTailoringFromPublicJobBoardStep.step1 &&
    isNewBuilderEnabled &&
    selectedPrefillOption &&
    editorPanelType === 'editor' &&
    isAboveTablet

  const renderButtons = () => {
    if (step === AutoTailoringFromPublicJobBoardStep.step1) {
      if (isNewBuilderEnabled && selectedPrefillOption && editorPanelType === 'customization') {
        return (
          <ButtonV2 onClick={() => handleTabChange('editor')} size={ButtonSize.small}>
            {i18n.t('builder.prefill_resume.auto_tailoring_view.header.continue')}
          </ButtonV2>
        )
      }

      return <StartTailoringButton />
    }

    if (step === AutoTailoringFromPublicJobBoardStep.step2) {
      return <TailoringFromPublicJobBoardStep2Buttons />
    }

    if (step === AutoTailoringFromPublicJobBoardStep.finished) {
      return <FinnishTailoringButton />
    }

    return null
  }

  const onBack = () => {
    const backUrl = mode === AutoTailoringMode.fromMemberJB ? '/job-search' : '/'

    if (step === AutoTailoringFromPublicJobBoardStep.step1) {
      if (isNewBuilderEnabled && selectedPrefillOption && editorPanelType === 'editor') {
        handleTabChange('customization')
        return
      }

      navigate(backUrl)
      return
    }

    if (step === AutoTailoringFromPublicJobBoardStep.step2) {
      return
    }

    if (step === AutoTailoringFromPublicJobBoardStep.finished) {
      navigate(backUrl)
      return
    }

    return null
  }

  const titleTranslation = useMemo(() => {
    if (
      step === AutoTailoringFromPublicJobBoardStep.step2 ||
      step === AutoTailoringFromPublicJobBoardStep.finished
    ) {
      return `title_tailor_resume`
    }

    if (isRbpStepFlowActive) {
      return `title_create_resume`
    }

    if (isNewBuilderEnabled && selectedPrefillOption && editorPanelType === 'customization') {
      return `title_choose_template`
    }

    return `title_review_resume`
  }, [step, isRbpStepFlowActive, selectedPrefillOption, editorPanelType, isNewBuilderEnabled])

  const stepNumber = useMemo(() => {
    if (isNewBuilderEnabled) {
      if (step !== AutoTailoringFromPublicJobBoardStep.step1) {
        return 3
      }

      if (editorPanelType === 'editor') {
        return 2
      }

      return 1
    }

    if (step === AutoTailoringFromPublicJobBoardStep.step1) {
      return 1
    }

    return 2
  }, [step, editorPanelType, isNewBuilderEnabled])

  if (isNewBuilderEnabled && !selectedPrefillOption && !isAboveTablet) {
    return null
  }

  return (
    <Container data-testid="tailoring-from-job-board-header" {...rest}>
      {step !== AutoTailoringFromPublicJobBoardStep.step2 ? (
        <BackButton onClick={onBack} data-testid="back-button">
          <BackIcon>
            <Icon24.ArrowRight />
          </BackIcon>
          {showBackToTemplatesButtonText &&
            i18n.t('builder.prefill_resume.auto_tailoring_view.header.title_choose_template')}
        </BackButton>
      ) : (
        <BackButtonPlaceholder />
      )}
      {isTabsVisible ? (
        <Center>
          <SegmentControls>
            {tabItems.map(item => (
              <StyledSegmentControl
                key={item.id}
                $isActive={editorPanelType === item.id}
                onClick={() => handleTabChange?.(item.id)}
              >
                {item.text}
              </StyledSegmentControl>
            ))}
          </SegmentControls>
        </Center>
      ) : (
        <Center data-testid="center-text">
          <Title>
            {i18n.t(`builder.prefill_resume.auto_tailoring_view.header.${titleTranslation}`)}
          </Title>

          <Step
            dangerouslySetInnerHTML={{
              __html: i18n.t(`builder.prefill_resume.auto_tailoring_view.header.step`, {
                startStep: stepNumber,
                endStep: isNewBuilderEnabled ? 3 : 2,
              }),
            }}
          />
        </Center>
      )}
      <WrapperStartTailoringButton>{renderButtons()}</WrapperStartTailoringButton>
    </Container>
  )
}
